/**
 * RoleConfig - Admin screen to assign permissions for specific buttons/actions to CPS roles.
 *
 * Rendered as part of Admin -> Role Config vertical tab.
 * Lets an admin check/uncheck each button access for both the Admin role and the User role.
 *
 * Business rules:
 *  - The "Admin -> Role Config -> Save" permission is always granted to the Admin role and is
 *    not editable (checked + disabled).
 *  - Cancel resets any unsaved changes back to the last saved state.
 *  - Save persists the changes; the backend is responsible for storing them and sending the
 *    change-notification email (previous vs new values, changes highlighted in red).
 */
import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Typography, Button, Checkbox } from '@mui/material';
import { ExpandMore, ChevronRight, UnfoldMore, UnfoldLess } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { horizontalTabStyles } from '../sharedStyles';
import FloatingMessage from '../../../common/FloatingMessage';
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
import { permissionKey, parseRoleConfigResponse, ROLE_CONFIG_URL, useRolePermissions } from '../../../common/RolePermissions';
import { LoadingIndicator } from '../reports/reconcilationreport/MuiStyled';
import PermissionButton from '../PermissionButton';
import { ADMIN_PERMISSIONS } from '../../../common/RolePermissions';

/**
 * Definition of every configurable button/action grouped by menu and screen.
 * Sourced from the CPS Role Config specification.
 */
const ROLE_CONFIG_SECTIONS = [
    {
        menu: 'Home',
        screens: [
            { screen: 'Dashboard', buttons: ['Download CSV'] },
            { screen: 'Request Detail', buttons: ['Add Note', 'Audit Trail', 'Clone Request', 'Status Update', 'View Request', 'Jira Edit', 'Request Automated Edit'] },
            { screen: 'DownStream Applications', buttons: ['Create/Recreate', 'Reconsolidate'] },
            { screen: 'DownStream Applications - View Response', buttons: ['Edit Response'] },
            { screen: 'DownStream Applications - Consolidate Response', buttons: ['Reupload'] }
        ],
    },
    {
        menu: 'Tracking',
        screens: [
            { screen: 'Bulk Upload', buttons: ['Create Bulk Response'] },
            { screen: 'Daily Feed - FTP Systems', buttons: ['Regenerate File', 'Resend File'] },
            { screen: 'Daily Feed - Ingest Response', buttons: ['Ingest'] },
            { screen: 'Request Queue', buttons: ['Retry'] },
            { screen: 'Request Queue - View Request', buttons: ['Edit', 'Status Update'] },
        ],
    },
    {
        menu: 'Resources',
        screens: [
            { screen: 'Cert Mgmt', buttons: ['Add Cert', 'Edit', 'Delete'] },
            { screen: 'Limits', buttons: ['Add Limit', 'Edit', 'Delete'] },
        ],
    },
    {
        menu: 'Admin',
        screens: [
            { screen: 'Apps Scope - B2B Users', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Apps Scope - Customer', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Apps Scope - Driver', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Apps Scope - Employee', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Apps Scope - Survivor', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Apps Scope - TCI Customer', buttons: ['Dispatch', 'Edit'] },
            { screen: 'Auto Close Settings - B2B Users', buttons: ['Edit'] },
            { screen: 'Auto Close Settings - Customer', buttons: ['Edit'] },
            { screen: 'Auto Close Settings - Employee', buttons: ['Edit'] },
            { screen: 'Auto Close Settings - TCI Customer', buttons: ['Edit'] },
            { screen: 'Contacts', buttons: ['Edit'] },
            { screen: 'Create DSAR - Auto', buttons: ['Generate', 'Create'] },
            { screen: 'Create DSAR - Manual', buttons: ['Create'] },
            { screen: 'Escalation Frequency - Daily', buttons: ['Escalate Now', 'Save'] },
            { screen: 'Escalation Frequency - Weekly', buttons: ['Escalate Now', 'Save'] },
            { screen: 'Escalation Settings - B2B Users', buttons: ['Edit'] },
            { screen: 'Escalation Settings - Customer', buttons: ['Edit'] },
            { screen: 'Escalation Settings - Driver', buttons: ['Edit'] },
            { screen: 'Escalation Settings - Employee', buttons: ['Edit'] },
            { screen: 'Escalation Settings - Survivor', buttons: ['Edit'] },
            { screen: 'Escalation Settings - TCI Customer', buttons: ['Edit'] },
            { screen: 'FTP/JIRA Settings', buttons: ['Edit'] },
            { screen: 'Request Review - B2B Users', buttons: ['Edit'] },
            { screen: 'Request Review - Customer', buttons: ['Edit'] },
            { screen: 'Request Review - Employee', buttons: ['Edit'] },
            { screen: 'Request Review - Survivor', buttons: ['Edit'] },
            { screen: 'Request Review - TCI Customer', buttons: ['Edit'] },
            { screen: 'Response Review - B2B Users', buttons: ['Edit'] },
            { screen: 'Response Review - Customer', buttons: ['Edit'] },
            { screen: 'Response Review - Employee', buttons: ['Edit'] },
            { screen: 'Response Review - TCI Customer', buttons: ['Edit'] },
            { screen: 'Role Config', buttons: ['Save'] },
            { screen: 'System Settings - Data Dictionary', buttons: ['Add Key', 'Edit', 'Delete'] },
            { screen: 'System Settings - Litigation', buttons: ['Add VIN', 'Edit', 'Delete'] },
            { screen: 'System Settings - Retention', buttons: ['Edit'] },
            { screen: 'System Settings - User', buttons: ['Sync Users', 'Edit'] },
            { screen: 'Upstreams - Request', buttons: ['Edit'] },
            { screen: 'Upstreams - Response', buttons: ['Edit'] },
        ],
    },
];

// The single permission whose Admin-role access is mandatory and locked (checked + disabled).
const LOCKED_ADMIN_MENU = 'Admin';
const LOCKED_ADMIN_SCREEN = 'Role Config';
const LOCKED_ADMIN_BUTTON = 'Save';

const isLockedAdmin = (menu, screen, button) =>
    menu === LOCKED_ADMIN_MENU && screen === LOCKED_ADMIN_SCREEN && button === LOCKED_ADMIN_BUTTON;

/**
 * Build the default permission map (used before backend data is available / when it is empty).
 * Admin role defaults to granted, User role defaults to not granted. The locked Admin permission
 * is always granted.
 */
const buildDefaultPermissions = () => {
    const perms = {};
    ROLE_CONFIG_SECTIONS.forEach(({ menu, screens }) => {
        screens.forEach(({ screen, buttons }) => {
            buttons.forEach((button) => {
                perms[permissionKey(menu, screen, button)] = {
                    admin: true,
                    user: false,
                };
            });
        });
    });
    return perms;
};

/**
 * Overlay the permissions returned by the API on top of the defaults.
 * Accepts an array of { menu, screen, button, admin_role, user_role }.
 */
const mergeApiPermissions = (apiData) => {
    const perms = buildDefaultPermissions();
    const stored = parseRoleConfigResponse(apiData);
    Object.keys(stored).forEach((key) => {
        if (perms[key]) {
            perms[key] = stored[key];
        }
    });
    // Enforce the locked Admin permission regardless of stored value.
    perms[permissionKey(LOCKED_ADMIN_MENU, LOCKED_ADMIN_SCREEN, LOCKED_ADMIN_BUTTON)].admin = true;
    return perms;
};

// Convert the permission map into the flat array payload the backend expects.
const toPayload = (perms) => {
    const payload = [];
    ROLE_CONFIG_SECTIONS.forEach(({ menu, screens }) => {
        screens.forEach(({ screen, buttons }) => {
            buttons.forEach((button) => {
                const entry = perms[permissionKey(menu, screen, button)];
                payload.push({
                    menu,
                    screen,
                    button,
                    admin_role: entry.admin,
                    user_role: entry.user,
                });
            });
        });
    });
    return payload;
};

const useStyles = makeStyles(() => ({
    wrapper: {
        height: '100%',
        ...horizontalTabStyles.tabWrapper,
        '& .header': {
            padding: '10px 20px',
            fontSize: '1rem',
            fontWeight: 'bold',
        },
    },
    tabBody: {
        ...horizontalTabStyles.tabBody,
    },
    scrollArea: {
        maxHeight: 'calc(100vh - 320px)',
        overflowY: 'auto',
        paddingRight: '8px',
    },
    menuGroup: {
        marginBottom: '18px',
    },
    menuHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2a6a9e 100%)',
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.82rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        padding: '8px 14px',
        borderRadius: '6px 6px 0 0',
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': {
            background: 'linear-gradient(135deg, #264d73 0%, #3180b8 100%)',
        },
    },
    menuHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        '& svg': {
            fontSize: '1.1rem',
        },
    },
    menuBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.3px',
    },
    badgeAdded: {
        color: '#7bffb0',
    },
    badgeRemoved: {
        color: '#ffb3ab',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        fontSize: '0.8rem',
        '& th, & td': {
            border: '1px solid #dde4ef',
            padding: '2px 12px',
            height: '30px',
            lineHeight: '1.2',
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        '& thead th': {
            background: '#c7d7e8',
            color: '#1e3a5f',
            fontWeight: 700,
            borderBottom: '2px solid #7f9ed7',
        },
        '& td.center, & th.center': {
            textAlign: 'center',
        },
        '& .MuiCheckbox-root': {
            padding: '2px',
        },
    },
    colScreen: {
        width: '45%',
    },
    colButton: {
        width: '35%',
    },
    colRole: {
        width: '10%',
    },
    screenRowEven: {
        background: '#ffffff',
    },
    screenRowOdd: {
        background: '#eaf1f8',
    },
    screenCell: {
        fontWeight: 600,
        color: '#1e3a5f',
        verticalAlign: 'middle',
    },
    changedCell: {
        background: '#fdecea !important',
    },
    addedCell: {
        background: '#e6f4ea !important',
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '10px',
        flexWrap: 'wrap',
    },
    expandAllBtn: {
        textTransform: 'none',
        color: '#267db7 !important',
        borderColor: '#267db7 !important',
        fontSize: '0.72rem !important',
    },
    summaryBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flex: 1,
        minWidth: '260px',
        padding: '6px 14px',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        fontSize: '0.76rem',
        fontWeight: 600,
        color: '#5a6b7b',
    },
    summaryDirty: {
        border: '1px solid #f0c36d',
        background: '#fff8e6',
        color: '#8a6d1a',
    },
    summaryChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    summaryDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        display: 'inline-block',
    },
    dotAdded: {
        background: '#3fa563',
    },
    dotRemoved: {
        background: '#eb0a1e',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '16px',
        paddingTop: '12px',
        borderTop: '1px solid #e2e8f0',
    },
    saveBtn: {
        background: '#267db7 !important',
        color: '#fff !important',
        textTransform: 'none',
        '&.Mui-disabled': {
            background: '#b0c4d4 !important',
            color: '#fff !important',
        },
    },
    cancelBtn: {
        textTransform: 'none',
        borderColor: '#267db7 !important',
        color: '#267db7 !important',
    },
}));

const RoleConfig = () => {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const { refreshPermissions } = useRolePermissions();

    const [permissions, setPermissions] = useState(buildDefaultPermissions);
    const [savedPermissions, setSavedPermissions] = useState(buildDefaultPermissions);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [messageType, setMessageType] = useState('error');
    const [collapsedMenus, setCollapsedMenus] = useState({});

    const toggleError = (msg, type = 'error') => {
        setError(msg);
        setMessageType(type);
    };

    const fetchData = useCallback(() => {
        setIsLoading(true);
        axios.get(ROLE_CONFIG_URL, {
            headers: { Authorization: authToken },
        }).then((res) => {
            const merged = mergeApiPermissions(res.data);
            setPermissions(merged);
            setSavedPermissions(merged);
        }).catch((err) => {
            // If the endpoint is unavailable, fall back to defaults so the screen is still usable.
            console.log('Error fetching role config', err);
            const defaults = mergeApiPermissions([]);
            setPermissions(defaults);
            setSavedPermissions(defaults);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [authToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const isDirty = useMemo(
        () => JSON.stringify(permissions) !== JSON.stringify(savedPermissions),
        [permissions, savedPermissions]
    );

    const handleToggle = useCallback((menu, screen, button, role) => {
        if (!isAdmin) return;
        if (role === 'admin' && isLockedAdmin(menu, screen, button)) return;
        const key = permissionKey(menu, screen, button);
        setPermissions((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                [role]: !prev[key][role],
            },
        }));
    }, [isAdmin]);

    const handleCancel = useCallback(() => {
        setPermissions(savedPermissions);
    }, [savedPermissions]);

    const handleSave = useCallback(() => {
        setIsLoading(true);
        axios.post(ROLE_CONFIG_URL, { permissions: toPayload(permissions) }, {
            headers: { Authorization: authToken },
        }).then(() => {
            setSavedPermissions(permissions);
            refreshPermissions();
            toggleError('Role configuration saved successfully', 'success');
        }).catch((err) => {
            const msg = err.response?.data?.message || 'Failed to save role configuration. Please try again.';
            toggleError(msg, 'error');
        }).finally(() => {
            setIsLoading(false);
        });
    }, [authToken, permissions, refreshPermissions]);

    const changeType = useCallback((key, role) => {
        const current = permissions[key]?.[role];
        const saved = savedPermissions[key]?.[role];
        if (current === saved) return '';
        return current ? 'added' : 'removed';
    }, [permissions, savedPermissions]);

    const changeClass = useCallback((key, role) => {
        const type = changeType(key, role);
        if (type === 'added') return classes.addedCell;
        if (type === 'removed') return classes.changedCell;
        return '';
    }, [changeType, classes]);

    // Summary of pending (unsaved) changes, overall and per menu.
    const changeSummary = useMemo(() => {
        const summary = { added: 0, removed: 0, total: 0, byMenu: {} };
        ROLE_CONFIG_SECTIONS.forEach(({ menu, screens }) => {
            let menuCount = 0;
            screens.forEach(({ screen, buttons }) => {
                buttons.forEach((button) => {
                    const key = permissionKey(menu, screen, button);
                    ['admin', 'user'].forEach((role) => {
                        const current = permissions[key]?.[role];
                        const saved = savedPermissions[key]?.[role];
                        if (current !== saved) {
                            menuCount += 1;
                            summary.total += 1;
                            if (current) summary.added += 1;
                            else summary.removed += 1;
                        }
                    });
                });
            });
            summary.byMenu[menu] = menuCount;
        });
        return summary;
    }, [permissions, savedPermissions]);

    const toggleMenu = useCallback((menu) => {
        setCollapsedMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
    }, []);

    const allCollapsed = useMemo(
        () => ROLE_CONFIG_SECTIONS.every(({ menu }) => collapsedMenus[menu]),
        [collapsedMenus]
    );

    const toggleAll = useCallback(() => {
        if (allCollapsed) {
            setCollapsedMenus({});
        } else {
            const next = {};
            ROLE_CONFIG_SECTIONS.forEach(({ menu }) => { next[menu] = true; });
            setCollapsedMenus(next);
        }
    }, [allCollapsed]);

    return (
        <div className={classes.wrapper}>
            <FloatingMessage open={error !== ''} message={error} severity={messageType} onClose={() => setError('')} />
            <Typography variant="h6" className="header">
                ROLE CONFIG
            </Typography>
            <div className={classes.tabBody}>
                {isLoading && <LoadingIndicator />}
                <div className={classes.toolbar}>
                    <div className={`${classes.summaryBar} ${isDirty ? classes.summaryDirty : ''}`}>
                        {isDirty ? (
                            <>
                                <span>{changeSummary.total} unsaved change{changeSummary.total === 1 ? '' : 's'}:</span>
                                <span className={classes.summaryChip}>
                                    <span className={`${classes.summaryDot} ${classes.dotAdded}`} />
                                    {changeSummary.added} added
                                </span>
                                <span className={classes.summaryChip}>
                                    <span className={`${classes.summaryDot} ${classes.dotRemoved}`} />
                                    {changeSummary.removed} removed
                                </span>
                            </>
                        ) : (
                            <span>No unsaved changes</span>
                        )}
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        className={classes.expandAllBtn}
                        startIcon={allCollapsed ? <UnfoldMore /> : <UnfoldLess />}
                        onClick={toggleAll}
                    >
                        {allCollapsed ? 'Expand All' : 'Collapse All'}
                    </Button>
                </div>
                <div className={classes.scrollArea}>
                    {ROLE_CONFIG_SECTIONS.map(({ menu, screens }) => {
                        const collapsed = Boolean(collapsedMenus[menu]);
                        const menuChanges = changeSummary.byMenu[menu] || 0;
                        return (
                        <div className={classes.menuGroup} key={menu}>
                            <div
                                className={classes.menuHeader}
                                onClick={() => toggleMenu(menu)}
                                role="button"
                                aria-expanded={!collapsed}
                            >
                                <span className={classes.menuHeaderLeft}>
                                    {collapsed ? <ChevronRight /> : <ExpandMore />}
                                    {menu}
                                </span>
                                {menuChanges > 0 && (
                                    <span className={classes.menuBadge}>
                                        {menuChanges} change{menuChanges === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                            {!collapsed && (
                            <table className={classes.table}>
                                <colgroup>
                                    <col className={classes.colScreen} />
                                    <col className={classes.colButton} />
                                    <col className={classes.colRole} />
                                    <col className={classes.colRole} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Screen</th>
                                        <th>Button</th>
                                        <th className="center">Admin Role</th>
                                        <th className="center">User Role</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {screens.map(({ screen, buttons }, screenIndex) => {
                                        const rowShade = screenIndex % 2 === 0 ? classes.screenRowEven : classes.screenRowOdd;
                                        return buttons.map((button, buttonIndex) => {
                                            const key = permissionKey(menu, screen, button);
                                            const entry = permissions[key] || { admin: false, user: false };
                                            const locked = isLockedAdmin(menu, screen, button);
                                            return (
                                                <tr key={key} className={rowShade}>
                                                    {buttonIndex === 0 && (
                                                        <td className={classes.screenCell} rowSpan={buttons.length}>
                                                            {screen}
                                                        </td>
                                                    )}
                                                    <td>{button}</td>
                                                    <td className={`center ${changeClass(key, 'admin')}`}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={entry.admin}
                                                            disabled={!isAdmin || locked}
                                                            onChange={() => handleToggle(menu, screen, button, 'admin')}
                                                            inputProps={{ 'aria-label': `${menu} ${screen} ${button} Admin Role` }}
                                                        />
                                                    </td>
                                                    <td className={`center ${changeClass(key, 'user')}`}>
                                                        <Checkbox
                                                            size="small"
                                                            checked={entry.user}
                                                            disabled={!isAdmin}
                                                            onChange={() => handleToggle(menu, screen, button, 'user')}
                                                            inputProps={{ 'aria-label': `${menu} ${screen} ${button} User Role` }}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    })}
                                </tbody>
                            </table>
                            )}
                        </div>
                        );
                    })}
                </div>

                    <div className={classes.footer}>
                        <Button
                            variant="outlined"
                            className={classes.cancelBtn}
                            disabled={!isDirty || isLoading}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <PermissionButton
                            permission={ADMIN_PERMISSIONS.ROLE_CONFIG_SAVE}
                            variant="contained"
                            className={classes.saveBtn}
                            disabled={!isDirty || isLoading}
                            onClick={handleSave}
                        >
                            Save
                        </PermissionButton>
                    </div>

            </div>
        </div>
    );
};

export default RoleConfig;
